/**
 * П5 (2026-07-24): «Розрахунки за документами» — розріз кожної накладної
 * (FIFO-закриття оплат, аналог УТП «по документах розрахунків»).
 *
 * Показує ВІДКРИТІ (неоплачені) накладні з простроченням: сторона
 * покупців (реалізації) / постачальників (надходження). Строк оплати —
 * дата документа + відстрочка договору (payment_term_days).
 *
 * 2026-08-22: серверна пагінація + сортування (InfiniteDocGrid);
 * підсумки «разом борг / прострочено» — з сервера (по ВСІЙ вибірці).
 *
 * ч.1033 (запит власника): галочки по рядках — над таблицею сума залишку
 * боргу ПОЗНАЧЕНИХ документів (напр. які накладні оплатити одним платежем).
 */
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useStickyState } from '@/hooks/useStickyFilters';
import { hasLanding, settlementsLanding } from '@/pages/reportLanding';
import type { ColDef, GridApi } from 'ag-grid-community';
import { reports, type SettlementByDocumentRow } from '@/api/documents';
import { docsWord, settlementDocLabel, settlementRowKey, sumOpenAmount } from '@/pages/settlementsSelection';
import { DOC_ROUTES } from '@/shared/utils/docRoutes';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { ContractorFilter } from '@/shared/components/DocListFilters';

const fmt = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Сума не рветься посеред числа («2 850 000,00 / грн») на великому шрифті —
// переноситься лише між блоками підсумків (ч.1033).
const Amount = ({ children }: { children: ReactNode }) =>
  <strong style={{ whiteSpace: 'nowrap' }}>{children}</strong>;

export default function SettlementsByDocumentsPage() {
  const { availableOrgs, hasPermissionInAnyOrg, loading: permsLoading } =
    usePermissionsByOrg('reports.regulated.view');
  // ч.951 (баг власника): фільтри переживають перехід на іншу сторінку.
  const [orgId, setOrgId] = useStickyState('settlements-by-documents.orgId', '');
  const [side, setSide] = useStickyState<'receivable' | 'payable'>(
    'settlements-by-documents.side', 'receivable');
  const [contractorId, setContractorId] = useStickyState(
    'settlements-by-documents.contractorId', '');
  const [totals, setTotals] = useState<{
    open: string; overdue: string; advances: string; unattributed: string;
  } | null>(null);

  const effectiveOrg = orgId || availableOrgs[0]?.id || '';

  // ч.1035: перехід з віджета «Борг …» головної («прострочено за
  // накладними») — сторона й організація з адреси, одноразово.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (!hasLanding(searchParams)) return;
    const l = settlementsLanding(searchParams);
    if (l.side) setSide(l.side);
    if (l.organization_id) setOrgId(l.organization_id);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ч.1033: позначені рядки → сума над таблицею.
  const apiRef = useRef<GridApi<SettlementByDocumentRow> | null>(null);
  const [selected, setSelected] = useState<SettlementByDocumentRow[]>([]);
  const clearSelection = () => {
    apiRef.current?.deselectAll();
    setSelected([]);
  };
  // Інша сторона / організація / контрагент — це інший перелік документів:
  // позначки скидаємо. Сам ag-Grid цього НЕ робить, коли задано getRowId
  // (тримає вибір між перезавантаженнями) — без скидання сума показувала б
  // документи попередньої вибірки, яких на екрані вже немає.
  useEffect(() => {
    apiRef.current?.deselectAll();
    setSelected((prev) => (prev.length ? [] : prev));
  }, [effectiveOrg, side, contractorId]);

  const { datasource, total } = useInfiniteGridDatasource<SettlementByDocumentRow>(
    async (limit, offset, sort) => {
      if (!effectiveOrg) return { items: [], total: 0 };
      const page = await reports.settlementsByDocuments({
        organization_id: effectiveOrg,
        side,
        contractor_id: contractorId || undefined,
        sort_by: sort?.sort_by,
        sort_dir: sort?.sort_dir,
        limit, offset,
      });
      setTotals({ open: page.total_open, overdue: page.total_overdue,
                  advances: page.total_advances ?? '0',
                  unattributed: page.total_unattributed ?? '0' });
      return page;
    },
    [effectiveOrg, side, contractorId],
  );

  const cols = useMemo<ColDef<SettlementByDocumentRow>[]>(() => [
    {
      colId: '_sel', headerName: '', width: 40,
      headerTooltip: 'Позначте документи — над таблицею зʼявиться їх залишок боргу',
      sortable: false, resizable: false, suppressMovable: true,
      checkboxSelection: true,
    },
    {
      colId: 'document', headerName: 'Документ', width: 240,
      valueGetter: (p) => p.data?.document_date ?? '',
      cellRenderer: (p: { data?: SettlementByDocumentRow }) => {
        if (!p.data) return '';
        // ч.1039: борг відкриває будь-який документ (довідка, коригування,
        // залишки, акт…) — підпис за типом; клік веде у сам документ.
        const to = DOC_ROUTES[p.data.settlement_doc_type]?.(p.data.settlement_doc_id);
        const label = (
          <>
            {settlementDocLabel(p.data.settlement_doc_type)}{' '}
            <strong>{p.data.document_number ?? '—'}</strong>
            {p.data.document_date && (
              <span style={{ color: 'var(--muted)' }}>
                {' '}від {new Date(p.data.document_date).toLocaleDateString('uk-UA')}
              </span>
            )}
          </>
        );
        return to ? <Link to={to}>{label}</Link> : <span>{label}</span>;
      },
    },
    {
      colId: 'contractor', headerName: 'Контрагент', flex: 1, minWidth: 180,
      valueGetter: (p) => p.data?.contractor_name ?? '—',
    },
    {
      colId: 'contract', headerName: 'Договір', flex: 1, minWidth: 160,
      valueGetter: (p) => p.data?.contract_name ?? '—',
    },
    {
      colId: 'total', headerName: 'Сума', width: 120,
      cellStyle: { textAlign: 'right' },
      valueGetter: (p) => p.data ? fmt(p.data.total_amount) : '',
    },
    {
      colId: 'paid', headerName: 'Сплачено', width: 120,
      cellStyle: { textAlign: 'right' },
      valueGetter: (p) => p.data ? fmt(p.data.paid_amount) : '',
    },
    {
      colId: 'open', headerName: 'Залишок боргу', width: 130,
      cellStyle: { textAlign: 'right' },
      cellRenderer: (p: { data?: SettlementByDocumentRow }) =>
        p.data ? <strong>{fmt(p.data.open_amount)}</strong> : '',
    },
    {
      colId: 'due', headerName: 'Строк оплати', width: 120,
      cellRenderer: (p: { data?: SettlementByDocumentRow }) => {
        if (!p.data) return '';
        return p.data.due_date
          ? new Date(p.data.due_date).toLocaleDateString('uk-UA')
          : <span style={{ color: 'var(--muted)' }} title="У договорі не задана відстрочка платежу">—</span>;
      },
    },
    {
      colId: 'overdue', headerName: 'Прострочено', width: 120,
      cellStyle: { textAlign: 'right' },
      cellRenderer: (p: { data?: SettlementByDocumentRow }) => {
        if (!p.data) return '';
        return p.data.overdue_days > 0 ? (
          <span style={{ color: 'var(--badge-red-fg)', fontWeight: 600 }}>{p.data.overdue_days} дн.</span>
        ) : (
          <span style={{ color: 'var(--badge-green-fg)' }}>—</span>
        );
      },
    },
  ], [side]);

  if (!permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <p style={{ color: 'var(--muted)' }}>
          Немає права перегляду звітів у жодній організації.
        </p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          Розрахунки за документами<TitleTotal total={total} />
          <span style={{ fontSize: 13, color: 'var(--muted)', marginLeft: 8, fontWeight: 400 }}>
            (FIFO-закриття, як в 1С)
          </span>
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <div className="seg-tabs">
            <button type="button" className={side === 'receivable' ? 'active' : ''}
              onClick={() => setSide('receivable')}>Борги покупців</button>
            <button type="button" className={side === 'payable' ? 'active' : ''}
              onClick={() => setSide('payable')}>Наші борги постачальникам</button>
          </div>
          <select value={effectiveOrg} onChange={(e) => setOrgId(e.target.value)}>
            {availableOrgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          {/* 2026-07-31 (рішення власника): пошуковий комбобокс «по парі
              букв» замість простині <select>. */}
          <ContractorFilter value={contractorId} onChange={setContractorId}
            label="контрагент" />
        </div>
      </div>

      <div style={{
        display: 'flex', flexWrap: 'wrap', columnGap: '1.5rem', rowGap: '0.25rem',
        margin: '0.5rem 0 0.75rem', fontSize: '0.9rem',
      }}>
        <span>Відкритих документів: <Amount>{total ?? '…'}</Amount></span>
        {totals && <span>За документами: <Amount>{fmt(totals.open)} грн</Amount></span>}
        {totals && Number(totals.overdue) > 0 && (
          <span style={{ color: 'var(--badge-red-fg)' }}>
            з них прострочено: <Amount>{fmt(totals.overdue)} грн</Amount>
          </span>
        )}
        {/* ч.1039: реєстр веде ВСІ документи розрахунків (і 685/377 — теж
            документами), тож «за документами» − «аванси» = чисте сальдо
            контрагентів, як в акті звірки. Аванси — оплати/довідки, ще не
            зараховані на жоден документ. «Не віднесено» — проводки на
            рахунках розрахунків без контрагента: у розріз (і в акт) вони не
            потрапляють — показуємо, щоб бухгалтер бачив, що лишилось поза. */}
        {totals && Number(totals.advances) !== 0 && (
          <span title="Оплати (аванси), ще не зараховані на жоден документ.
Чисте сальдо = за документами − аванси; саме воно збігається з актом звірки.">
            аванси не зараховані: <Amount>{fmt(totals.advances)} грн</Amount>
            {' '}· сальдо: <Amount>{fmt(String(Number(totals.open) - Number(totals.advances)))} грн</Amount>
          </span>
        )}
        {totals && Number(totals.unattributed) !== 0 && (
          <span title="Проводки на рахунках розрахунків без контрагента
(довідки з 1С без субконто, зарплатні утримання на 685 тощо).
У розріз по документах і в акт звірки вони не потрапляють.">
            не віднесено на контрагента: <Amount>{fmt(totals.unattributed)} грн</Amount>
          </span>
        )}
      </div>

      <InfiniteDocGrid<SettlementByDocumentRow>
        datasource={datasource}
        columnDefs={cols}
        storageKey="settlements-by-documents"
        rowSelection="multiple"
        onSelectionChanged={setSelected}
        getRowId={settlementRowKey}
        apiRef={apiRef}
        headerLeft={selected.length > 0 ? (
          <span className="sel-sum">
            <span>позначено {selected.length} {docsWord(selected.length)}</span>
            <span>·</span>
            <span>залишок боргу: <Amount>{fmt(sumOpenAmount(selected))} грн</Amount></span>
            <span>·</span>
            <button type="button" className="btn-link" onClick={clearSelection}>
              зняти позначки
            </button>
          </span>
        ) : (
          <span className="sel-sum sel-sum--empty">
            позначте документи галочками — тут буде їх сума
          </span>
        )}
      />

      <p style={{ marginTop: '0.75rem', color: 'var(--muted)', fontSize: '0.82rem' }}>
        Оплати розкладаються по накладних автоматично за принципом «перша
        виникла — перша закривається» (FIFO); оплата з полем «На підставі
        замовлення» спершу закриває накладні свого замовлення. Строк оплати
        = дата документа + відстрочка з договору.
      </p>
    </div>
  );
}
